/**
 * LIFF wrapper — provides typed access to LINE LIFF SDK.
 * Falls back to mock in dev (when not running inside LINE).
 */
import liff from '@line/liff';

export const LIFF_ID = (import.meta.env.VITE_LIFF_ID ?? '').trim();
export const LIFF_URL = LIFF_ID ? `https://liff.line.me/${LIFF_ID}` : '';
const USE_MOCK = !LIFF_ID || import.meta.env.DEV;

interface LiffShim {
  isLoggedIn: () => boolean;
  isInClient: () => boolean;
  login: (opts?: { redirectUri?: string }) => void;
  logout: () => void;
  getIDToken: () => string | null;
  getProfile: () => Promise<{ userId: string; displayName: string; pictureUrl?: string }>;
  closeWindow: () => void;
  scanCodeV2?: () => Promise<{ value: string }>;
}

const mockProfile = {
  userId: 'U_mock_dev_user',
  displayName: 'Dev User (Mock)',
  pictureUrl: undefined,
};

const mockShim: LiffShim = {
  isLoggedIn: () => true,
  isInClient: () => false,
  login: () => console.log('[mock] login'),
  logout: () => console.log('[mock] logout'),
  getIDToken: () => 'mock_id_token_for_local_dev',
  getProfile: async () => mockProfile,
  closeWindow: () => console.log('[mock] closeWindow'),
};

let activeShim: LiffShim = mockShim;
let initialized = false;

export async function initLiff(): Promise<void> {
  if (initialized) return;

  if (USE_MOCK) {
    activeShim = mockShim;
    initialized = true;
    return;
  }

  // ⚠️ ห้ามใส่ withLoginOnExternalBrowser: true — option นี้ทำให้ liff.init() เอง
  // auto-login (redirect ไป LINE) บนเบราว์เซอร์ทันที = คนเดสก์ท็อปเข้าไม่ได้.
  // เอาออก (default false) → init เฉยๆ ไม่เด้ง. ปุ่ม "เข้าด้วย LINE" เรียก liff.login()
  // เองตอนผู้ใช้กด (ทำงานบนเบราว์เซอร์ได้ปกติ).
  await liff.init({ liffId: LIFF_ID });
  // ไม่ auto liff.login() แล้ว — เดิมมันเด้งคนที่เปิดผ่านเบราว์เซอร์ไป LINE OAuth ทันที
  // ทำให้ไปไม่ถึงหน้า user/pass. ตอนนี้ปล่อยให้หน้า login เสนอ 2 ทางเลือก (user/pass หรือ
  // กดปุ่ม "เข้าด้วย LINE"/"สมัครทดลองใช้" เอง). ใน LINE client ปกติ logged in อยู่แล้ว →
  // getIDToken มีค่า → auto-login; ถ้าไม่ login ก็เห็นหน้า login + ปุ่ม LINE.
  activeShim = liff as unknown as LiffShim;
  initialized = true;
}

export function getIDToken(): string | null {
  // When the web-login flow skips initLiff() in production, activeShim
  // stays at its mockShim default — which would happily return a fake
  // token and trick useAuth into hitting /auth/me with garbage (→ 401).
  // Guard: only trust the shim once initLiff has actually run (or when
  // we're in dev/mock mode by design).
  if (!initialized && !USE_MOCK) return null;
  return activeShim.getIDToken();
}

const IDTOKEN_RELOGIN_KEY = 'idtoken_relogin_at';

/**
 * Return a usable LINE idToken, re-running LINE login to mint one when it is
 * missing. A null idToken means there is no LINE login session yet — e.g. the
 * page was opened as the raw Pages URL instead of the LIFF link, or the session
 * lapsed. Instead of dead-ending on "ไม่พบ idToken", trigger `liff.login()` which
 * redirects to LINE and returns with a fresh idToken (same recovery api-client
 * already uses). A 60s sessionStorage guard prevents an infinite login↔reload
 * loop when login still can't produce a token (e.g. missing openid scope).
 *
 * Returns the idToken, or null when a login redirect was just triggered (the
 * caller should stop and let the page reload) OR we already tried this session.
 */
export function ensureIdToken(): string | null {
  const token = getIDToken();
  if (token) return token;
  if (USE_MOCK) return null;
  try {
    const at = Number(sessionStorage.getItem(IDTOKEN_RELOGIN_KEY) ?? '0');
    if (at > 0 && Date.now() - at < 60_000) return null; // already tried — don't loop
    sessionStorage.setItem(IDTOKEN_RELOGIN_KEY, String(Date.now()));
  } catch {
    /* ignore storage quota / privacy-mode failures */
  }
  activeShim.login({ redirectUri: window.location.href }); // redirects → returns with fresh idToken
  return null;
}

/** Logged into the LINE session (so `login()` can silently mint a fresh
 *  idToken without a password). False on the web-login / unauthenticated path. */
export function isLoggedIn(): boolean {
  return activeShim.isLoggedIn();
}

/**
 * Decode the LINE idToken's `exp` and decide whether it is past (or about to
 * pass) its lifetime. LIFF's `getIDToken()` returns the token minted at login
 * time and does NOT auto-refresh it — once the channel's "ID token expiration"
 * elapses, LINE's /oauth2/v2.1/verify rejects it with 400 "IdToken expired."
 * and the worker answers AUTH_FAILED "invalid id token". Detecting this lets
 * the caller re-run `liff.login()` to get a fresh token instead of dead-ending
 * on the error screen. Treat undecodable tokens as expired (force a refresh).
 * The 60s skew refreshes slightly early so a borderline token doesn't slip
 * through and fail server-side.
 */
export function isIdTokenExpired(token: string | null | undefined): boolean {
  if (USE_MOCK) return false; // mock token is always accepted by the mock path
  if (!token) return true;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return true;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof payload.exp !== 'number') return true;
    const SKEW_SEC = 60;
    return payload.exp * 1000 <= Date.now() + SKEW_SEC * 1000;
  } catch {
    return true;
  }
}

export async function getProfile() {
  return activeShim.getProfile();
}

export function logout() {
  activeShim.logout();
}

export function login(redirectUri?: string) {
  activeShim.login({ redirectUri: redirectUri ?? window.location.href });
}

/** อยู่ในแอป LINE หรือไม่ (หลัง initLiff). ใช้ตัดสินใจ UX หน้า login. */
export function isInLineClient(): boolean {
  return activeShim.isInClient();
}

export function closeWindow() {
  activeShim.closeWindow();
}

export async function scanQR(): Promise<string | null> {
  if (activeShim.scanCodeV2) {
    const result = await activeShim.scanCodeV2();
    return result.value;
  }
  return null;
}

export const isMock = USE_MOCK;
